import ImageCropperModal from '../components/ImageCropperModal';
import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import * as XLSX from 'xlsx';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Crop, Search, Filter, Download, Star, ExternalLink, ChevronRight, MessageSquare, ShieldAlert, ShieldCheck, Shield, Loader, Check, ShoppingBag } from 'lucide-react';



function extractTrailingJSON(text) {
    const lastClose = text.lastIndexOf(']');
    if (lastClose === -1) return null;
    let idx = text.indexOf('[');
    while (idx !== -1 && idx < lastClose) {
        try {
            const parsed = JSON.parse(text.substring(idx, lastClose + 1));
            if (Array.isArray(parsed) && parsed.length > 0 && (parsed[0].url || parsed[0].link || parsed[0].URL || parsed[0].Url)) {
                return { parsed, startIdx: idx, endIdx: lastClose };
            }
        } catch (e) { }
        idx = text.indexOf('[', idx + 1);
    }
    return null;
}

const ENGINE_ICONS = {
  /* INJECTED_MB_PC_LOGOS_v2 */
  '豆包': '/icons/doubao.png',
  '豆包(PC)': '/icons/doubao.png',
  '豆包(MB)': '/icons/doubao.png',
  '豆包（PC）': '/icons/doubao.png',
  '豆包（MB）': '/icons/doubao.png',
  'doubao': '/icons/doubao.png',
  '腾讯元宝(PC)': '/icons/yuanbao.png',
  '腾讯元宝(MB)': '/icons/yuanbao.png',
  '腾讯元宝（PC）': '/icons/yuanbao.png',
  '腾讯元宝（MB）': '/icons/yuanbao.png',
  '通义千问(PC)': '/icons/tongyi.png',
  '通义千问(MB)': '/icons/tongyi.png',
  '通义千问（PC）': '/icons/tongyi.png',
  '通义千问（MB）': '/icons/tongyi.png',
  '通义': '/icons/tongyi.png',
  '文心一言': '/icons/wenxin.png',
  '文心一言(PC)': '/icons/wenxin.png',
  '文心一言(MB)': '/icons/wenxin.png',
  'Deepseek': '/icons/deepseek.png',
  'DeepSeek': '/icons/deepseek.png',
  'deepseek': '/icons/deepseek.png',
  'Kimi': '/icons/kimi.png',
  'kimi': '/icons/kimi.png',
  '元宝': '/icons/yuanbao.png',
  '腾讯元宝': '/icons/yuanbao.png',
  '百度AI搜索': '/icons/baidu.png',
  '百度': '/icons/baidu.png',
  '小红书AI': '/icons/xiaohongshu.png',
  '抖音AI': '/icons/douyin.png',
  '小红书': '/icons/xiaohongshu.png',
  'xhs': '/icons/xiaohongshu.png',
  '微信搜一搜': '/icons/wechat.png',
  '微信': '/icons/wechat.png',
};

const getEngineIcon = (name) => ENGINE_ICONS[name] || null;

export default function GeoTaskResults() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedResult, setSelectedResult] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterEngines, setFilterEngines] = useState([]);
  const [filterStatuses, setFilterStatuses] = useState([]);
  const [showEngineDropdown, setShowEngineDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleted, setShowDeleted] = useState(false);
  const [zoomedImage, setZoomedImage] = useState(null);
    const [cropTarget, setCropTarget] = useState(null);

    const handleCropComplete = async (base64Image) => {
        if (!cropTarget) return;
        try {
            // Because GeoTaskResults doesn't have axios imported globally by default maybe? Wait, it does.
            const resRaw = await fetch(`/api/results/${cropTarget.recordId}/update-card-image`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                body: JSON.stringify({
                    cardIndex: cropTarget.cardIndex,
                    base64Image
                })
            });
            const res = { data: await resRaw.json() };
            if (res.data.success) {
                if (selectedResult && selectedResult.id === cropTarget.recordId) {
                    const newCards = [...(typeof selectedResult.productCards === 'string' ? JSON.parse(selectedResult.productCards) : selectedResult.productCards)];
                    newCards[cropTarget.cardIndex].imageUrl = res.data.newImageUrl;
                    setSelectedResult({ ...selectedResult, productCards: JSON.stringify(newCards) });
                }
                setResults(prev => prev.map(item => {
                    if (item.id === cropTarget.recordId) {
                        const newCards = [...(typeof item.productCards === 'string' ? JSON.parse(item.productCards) : item.productCards)];
                        newCards[cropTarget.cardIndex].imageUrl = res.data.newImageUrl;
                        return { ...item, productCards: JSON.stringify(newCards) };
                    }
                    return item;
                }));
                setCropTarget(null);
            }
        } catch (e) {
            console.error('Crop save error', e);
            alert('保存截图失败');
        }
    };

  const [selectedIds, setSelectedIds] = useState([]);
  const PAGE_SIZE = 50;

  useEffect(() => {
    fetchResults();
    const handleGlobalFilterChange = () => fetchResults();
    window.addEventListener('globalFilterChange', handleGlobalFilterChange);
    return () => window.removeEventListener('globalFilterChange', handleGlobalFilterChange);
  }, [id, showDeleted]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.filter-dropdown')) {
        setShowEngineDropdown(false);
        setShowStatusDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleEngineFilter = (engine) => {
    setFilterEngines(prev => 
      prev.includes(engine) ? prev.filter(e => e !== engine) : [...prev, engine]
    );
  };

  const toggleStatusFilter = (status) => {
    setFilterStatuses(prev => 
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    );
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterEngines, filterStatuses]);

  const fetchResults = async () => {
    setLoading(true);
    try {
      let url = `/api/results?limit=1000&deleted=${showDeleted}&fetchMode=${localStorage.getItem('globalFetchMode') || 'hybrid'}`;
      if (id) {
        url += `&geoTaskId=${id}`;
      } else {
        const globalBrand = localStorage.getItem('globalBrand');
        const globalProject = localStorage.getItem('globalProject');
        if (globalBrand && globalBrand !== 'all' && globalBrand !== 'undefined') {
          url += `&brandProjectId=${globalBrand}`;
        }
        if (globalProject && globalProject !== 'all' && globalProject !== 'undefined') {
          url += `&geoTaskId=${globalProject}`;
        }
      }
      
      const token = localStorage.getItem('token');
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        // Map backend model to frontend structure
        const mappedData = data.data.map(item => ({
          id: item.id,
          time: new Date(item.createdAt).toLocaleString('zh-CN'),
          fetchMode: item.geoTask?.fetchMode || 'api',
          platform: item.engineName,
          persona: item.personaName || '无',
          theme: item.themeName || '默认',
          question: item.question,
          mentioned: item.isBrandMentioned,
          sentiment: item.sentiment || 'neutral',
          brands: item.mentionedBrands ? JSON.parse(item.mentionedBrands) : [],
          citations: item.citations ? JSON.parse(item.citations) : [],
          answer: item.answer,
          sources: (() => {
            let arr = [];
            if (item.citations) {
              try { 
                const p = typeof item.citations === 'string' ? JSON.parse(item.citations) : item.citations;
                if (Array.isArray(p)) arr = arr.concat(p);
              } catch(e) { console.error('parse citations error', e); }
            }
            if (item.sources) {
              try { 
                const p = typeof item.sources === 'string' ? JSON.parse(item.sources) : item.sources;
                if (Array.isArray(p)) arr = arr.concat(p);
              } catch(e) { console.error('parse sources error', e); }
            }
            if (item.mediaSources && Array.isArray(item.mediaSources)) {
               arr = arr.concat(item.mediaSources);
            }
            if (item.answer) {
               const extracted = extractTrailingJSON(item.answer);
               if (extracted) arr = arr.concat(extracted.parsed);
            }
            // Remove duplicates by url
            const uniqueUrls = new Set();
            return arr.filter(s => {
                const link = s.url || s.link;
                if (link && uniqueUrls.has(link)) return false;
                if (link) uniqueUrls.add(link);
                return true;
            }).map(s => ({...s, name: s.name || s.mediaName || s.site_name || s.title || '引用来源'}));
          })(),
          productCards: item.productCards || '[]',
          engineId: item.engineId || '',
          engineName: item.engineName || '',
          xhsBrandDetails: item.xhsBrandDetails || [],
          xhsRawNotes: item.xhsRawNotes || []
        }));
        setResults(mappedData);
      }
    } catch (error) {
      console.error('Failed to fetch results:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSentimentBadge = (sentiment, mentioned) => {
    if (!mentioned) return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"><ShieldAlert size={12} className="mr-1"/>防守失守</span>;
    if (sentiment === 'positive') return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"><ShieldCheck size={12} className="mr-1"/>首选推荐</span>;
    if (sentiment === 'controversial') return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400"><Shield size={12} className="mr-1"/>争议/多面</span>;
    if (sentiment === 'neutral') return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400"><Shield size={12} className="mr-1"/>客观对比</span>;
    return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"><ShieldAlert size={12} className="mr-1"/>防守失守</span>;
  };

  
  
  
  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(paginatedResults.map(r => r.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleBatchDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`确定要删除选中的 ${selectedIds.length} 条记录吗？`)) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/results/batch-delete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ids: selectedIds })
      });
      const data = await res.json();
      if (data.success) {
        alert('删除成功');
        setSelectedIds([]);
        if (selectedResult && selectedIds.includes(selectedResult.id)) {
          setSelectedResult(null);
        }
        fetchResults();
      } else {
        alert(data.message || '删除失败');
      }
    } catch (e) {
      console.error(e);
      alert('请求报错');
    }
  };

  const handleRetryFailed = async () => {
    const failedIds = results.filter(r => r.status === 'failed').map(r => r.id);
    if (failedIds.length === 0) {
      alert('当前大盘中没有失败的任务');
      return;
    }
    
    if (!window.confirm(`确定要重跑这 ${failedIds.length} 个失败项吗？这将会消耗对应的调用额度。`)) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/results/retry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ids: failedIds })
      });
      const data = await res.json();
      if (data.success) {
        alert(data.message || '重试已提交，请稍后刷新查看进度');
        fetchResults(); // reload
      } else {
        alert(data.message || '重试提交失败');
      }
    } catch (e) {
      console.error(e);
      alert('请求报错');
    }
  };

  
  const handleForceApi = async () => {
    if (selectedIds.length === 0) {
      alert('请先勾选需要强制 API 直连补数的记录');
      return;
    }
    
    if (!window.confirm(`确定要将选中的 ${selectedIds.length} 条记录转为 API 直连模式并重跑吗？\n(建议仅对 RPA 抓取失败/超时/长期卡在 running 的记录使用)`)) return;
    
    try {
      const token = localStorage.getItem('token');
      let successCount = 0;
      
      // We process them one by one or batch. Since backend route is /api/results/:id/force-api, we do a loop.
      for (const id of selectedIds) {
          const res = await fetch(`/api/results/${id}/force-api`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          });
          const data = await res.json();
          if (data.success) {
             successCount++;
          }
      }
      
      alert(`已成功将 ${successCount} 条记录加入 API 重试队列，请稍后刷新查看进度。`);
      fetchResults();
      setSelectedIds([]);
    } catch (error) {
      console.error('Force API Error:', error);
      alert('操作失败');
    }
  };

const handleExportExcel = () => {
    if (filteredResults.length === 0) {
      alert('没有可以导出的数据');
      return;
    }

    const exportData = filteredResults.map((r, index) => {
      let cleanAnswer = r.answer || '';
      cleanAnswer = cleanAnswer.replace(/```json[\s\S]*?```/g, '');
      cleanAnswer = cleanAnswer.replace(/\[\s*\{[\s\S]*\}\s*\]\s*$/, '');
      cleanAnswer = cleanAnswer.trim();
      
      return {
        '序号': index + 1,
        'AI平台': r.platform,
        '提示词/问题': r.question,
        '状态': r.status === 'analyzed' ? '分析完成' : r.status === 'completed' ? '抓取完成' : r.status === 'failed' ? '抓取失败' : r.status,
        '是否提及本品': r.mentioned ? '是' : '否',
        '提及情感': r.sentiment === 'positive' ? '正向 (首选推荐)' : r.sentiment === 'controversial' ? '争议 (优缺参半)' : r.sentiment === 'neutral' ? '中性 (客观对比)' : r.sentiment === 'negative' ? '负面 (防守失守)' : '-',
        '提及的所有品牌': r.brands && r.brands.length > 0 ? r.brands.join('、') : '-',
        'AI回答原文': cleanAnswer ? (cleanAnswer.length > 32700 ? cleanAnswer.substring(0, 32700) + '...[内容已截断]' : cleanAnswer) : ''
      };
    });

    
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'GEO抓取大盘数据');

    // 生成信源引用sheet
    const citationsData = [];
    filteredResults.forEach((r, index) => {
      if (r.sources && r.sources.length > 0) {
        r.sources.forEach((s, idx) => {
          citationsData.push({
            '主表序号': index + 1,
            'AI平台': r.platform,
            '提示词/问题': r.question,
            '引用序号': idx + 1,
            '媒体/来源名称': s.name || s.mediaName || s.title || s.site_name || '',
            '文章标题': s.title || '',
            '链接URL': s.url || s.link || ''
          });
        });
      }
    });
    if (citationsData.length > 0) {
      const citationsSheet = XLSX.utils.json_to_sheet(citationsData);
      XLSX.utils.book_append_sheet(workbook, citationsSheet, '信源引用');
      
      const citCols = [
        { wch: 10 },
        { wch: 15 },
        { wch: 40 },
        { wch: 10 },
        { wch: 25 },
        { wch: 40 },
        { wch: 80 }
      ];
      citationsSheet['!cols'] = citCols;
    }


    // 调整列宽
    const wscols = [
      { wch: 6 },
      { wch: 15 },
      { wch: 40 },
      { wch: 10 },
      { wch: 12 },
      { wch: 15 },
      { wch: 20 },
      { wch: 100 }
    ];
    worksheet['!cols'] = wscols;

    XLSX.writeFile(workbook, `GEO抓取大盘数据_${new Date().getTime()}.xlsx`);
  };

  const uniqueEngines = [...new Set(results.map(r => r.platform))];

  const filteredResults = results.filter(r => {
    const matchesSearch = r.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          r.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          r.platform.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesEngine = filterEngines.length === 0 || filterEngines.includes(r.platform);
    
    const matchesStatus = filterStatuses.length === 0 || filterStatuses.some(status => {
      if (status === 'mentioned') return r.mentioned === true;
      if (status === 'unmentioned') return r.mentioned === false;
      if (status === 'positive') return r.sentiment === 'positive';
      if (status === 'negative') return r.sentiment === 'negative';
      if (status === 'controversial') return r.sentiment === 'controversial';
      return false;
    });

    return matchesSearch && matchesEngine && matchesStatus;
  });

  const totalPages = Math.ceil(filteredResults.length / PAGE_SIZE);
  const paginatedResults = filteredResults.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="max-w-full flex flex-col h-[calc(100vh-80px)]">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 shrink-0">
        <div className="flex items-center space-x-4">
          {id && <button onClick={() => navigate('/tasks')} className="p-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">
            <ArrowLeft size={20} className="text-gray-600 dark:text-slate-300" />
          </button>}
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">抓取结果大盘 {id ? `(任务: ${id})` : ""}</h1>
            <p className="text-sm text-gray-500 dark:text-slate-400">设置AI对话问题，系统自动获取对话结果并进行分析</p>
          </div>
        </div>
        <div className="flex space-x-3">
          
          
          {selectedIds.length > 0 && (
            <button onClick={handleBatchDelete} className="flex items-center px-4 py-2 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/50">
              批量删除 ({selectedIds.length})
            </button>
          )}
          <button onClick={handleForceApi} className="flex items-center px-4 py-2 bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-800 rounded-lg text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/50">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
            强制 API 补数 ({selectedIds.length})
          </button>
          <button onClick={handleRetryFailed} className="flex items-center px-4 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-sm font-medium text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
            重跑失败项
          </button>

          <button onClick={handleExportExcel} className="flex items-center px-4 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-sm font-medium text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700">
            <Download size={16} className="mr-2" />
            导出数据
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex space-x-6 min-h-0">
        {/* Left List */}
        <div className={`flex flex-col bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden ${selectedResult ? 'w-1/3' : 'w-full'}`}>
          {/* List Header/Search */}
          <div className="p-4 border-b border-gray-200 dark:border-slate-700 space-y-4 shrink-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="搜索问题、回答、平台..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-slate-900/50 border border-gray-200 dark:border-slate-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-white"
              />
            </div>
            
            <div className="flex space-x-2">
              {/* Engine Multi-select Dropdown */}
              <div className="relative filter-dropdown">
                <button 
                  onClick={() => { setShowEngineDropdown(!showEngineDropdown); setShowStatusDropdown(false); }}
                  className={`flex items-center px-3 py-1.5 border rounded-lg text-xs font-medium transition-colors ${
                    filterEngines.length > 0 
                      ? 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/30 dark:border-blue-800 dark:text-blue-400' 
                      : 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700'
                  }`}
                >
                  <Filter size={14} className="mr-1.5" />
                  引擎 {filterEngines.length > 0 ? `(${filterEngines.length})` : ''}
                </button>
                {showEngineDropdown && (
                  <div className="absolute left-0 mt-1 w-48 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-lg z-10 py-1">
                    {uniqueEngines.map(engine => (
                      <div 
                        key={engine}
                        onClick={() => toggleEngineFilter(engine)}
                        className="flex items-center px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700/50"
                      >
                        <div className={`w-4 h-4 rounded border mr-2 flex items-center justify-center ${
                          filterEngines.includes(engine) 
                            ? 'bg-blue-500 border-blue-500' 
                            : 'border-gray-300 dark:border-slate-600'
                        }`}>
                          {filterEngines.includes(engine) && <Check size={12} className="text-white" />}
                        </div>
                        {getEngineIcon(engine) && <img src={getEngineIcon(engine)} alt={engine} className="w-4 h-4 mr-2 object-contain" />}
                        <span className="text-sm text-gray-700 dark:text-slate-300">{engine}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Status Multi-select Dropdown */}
              <div className="relative filter-dropdown">
                <button 
                  onClick={() => { setShowStatusDropdown(!showStatusDropdown); setShowEngineDropdown(false); }}
                  className={`flex items-center px-3 py-1.5 border rounded-lg text-xs font-medium transition-colors ${
                    filterStatuses.length > 0 
                      ? 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/30 dark:border-blue-800 dark:text-blue-400' 
                      : 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700'
                  }`}
                >
                  <Shield size={14} className="mr-1.5" />
                  防守状态 {filterStatuses.length > 0 ? `(${filterStatuses.length})` : ''}
                </button>
                {showStatusDropdown && (
                  <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-lg z-10 py-1">
                    {[
                      { id: 'positive', label: '首选推荐 (正面)' },
                      { id: 'negative', label: '防守失守 (负面)' },
                      { id: 'mentioned', label: '提及品牌' },
                      { id: 'unmentioned', label: '未提及' },
                    ].map(status => (
                      <div 
                        key={status.id}
                        onClick={() => toggleStatusFilter(status.id)}
                        className="flex items-center px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700/50"
                      >
                        <div className={`w-4 h-4 rounded border mr-2 flex items-center justify-center ${
                          filterStatuses.includes(status.id) 
                            ? 'bg-blue-500 border-blue-500' 
                            : 'border-gray-300 dark:border-slate-600'
                        }`}>
                          {filterStatuses.includes(status.id) && <Check size={12} className="text-white" />}
                        </div>
                        <span className="text-sm text-gray-700 dark:text-slate-300">{status.label}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* List Items */}
          <div className="flex-1 flex flex-col min-h-0">
            <div className="flex-1 overflow-y-auto p-2">
              
            <div className="flex justify-between items-center px-2 pb-2 mb-2 border-b border-gray-100 dark:border-slate-700">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input 
                  type="checkbox" 
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  checked={paginatedResults.length > 0 && selectedIds.length === paginatedResults.length}
                  onChange={handleSelectAll}
                />
                <span className="text-xs text-gray-500 dark:text-slate-400">全选本页</span>
              </label>
            </div>
            {loading ? (

                 <div className="flex justify-center items-center h-full">
                   <Loader className="animate-spin text-blue-500" size={32} />
                 </div>
              ) : paginatedResults.length === 0 ? (
                 <div className="text-center text-gray-500 dark:text-slate-400 p-8">暂无数据</div>
              ) : (
                paginatedResults.map((result) => (
                <div 
                  key={result.id}
                  onClick={() => setSelectedResult(result)}
                  className={`p-4 rounded-lg cursor-pointer transition-colors mb-2 ${
                    selectedResult?.id === result.id 
                      ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' 
                      : 'hover:bg-gray-50 dark:hover:bg-slate-700/50 border-transparent'
                  } border`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center space-x-2">
                      <input 
                        type="checkbox" 
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        checked={selectedIds.includes(result.id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleSelect(result.id);
                        }}
                      />
                      {getEngineIcon(result.platform) ? (

                        <div className="flex items-center space-x-1 bg-gray-100 dark:bg-slate-700 px-2 py-0.5 rounded">
                          <img src={getEngineIcon(result.platform)} alt={result.platform} className="w-3 h-3 object-contain" />
                          <span className="text-xs font-medium text-gray-600 dark:text-slate-300">{result.platform}</span>
                        </div>
                      ) : (
                        <span className="text-xs font-medium px-2 py-0.5 bg-gray-100 dark:bg-slate-700 rounded text-gray-600 dark:text-slate-300">
                          {result.platform}
                        </span>
                      )}
                      <span className="text-xs text-gray-500 dark:text-slate-400">{result.time}</span>
                      {result.fetchMode === "rpa" ? (
                        <span className="text-[10px] font-medium px-1.5 py-0.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800 rounded">RPA</span>
                      ) : (
                        <span className="text-[10px] font-medium px-1.5 py-0.5 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800 rounded">API</span>
                      )}
                      {(() => {
                        let hasCards = false;
                        let count = 0;
                        try {
                          const parsed = typeof result.productCards === 'string' ? JSON.parse(result.productCards) : result.productCards;
                          if (Array.isArray(parsed) && parsed.length > 0) {
                            hasCards = true;
                            count = parsed.length;
                          }
                        } catch (e) {}
                        return hasCards ? (
                          <span className="text-[10px] font-medium px-1.5 py-0.5 bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-800 rounded flex items-center gap-1"><ShoppingBag size={10} /> 卡片x{count}</span>
                        ) : null;
                      })()}
                    </div>
                    {getSentimentBadge(result.sentiment, result.mentioned)}
                  </div>
                  
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2 line-clamp-2">
                    {result.question}
                  </h3>
                  
                  <div className="flex items-center space-x-3 text-xs text-gray-500 dark:text-slate-400">
                    <span className="flex items-center"><Star size={12} className="mr-1" /> {result.theme}</span>
                    <span className="flex items-center"><MessageSquare size={12} className="mr-1" /> {result.persona}</span>
                  </div>
                </div>
              )))}
            </div>
            
            {/* Pagination Controls */}
            {!loading && totalPages > 1 && (
              <div className="p-3 border-t border-gray-200 dark:border-slate-700 flex items-center justify-between shrink-0 bg-white dark:bg-slate-800">
                <span className="text-xs text-gray-500 dark:text-slate-400">
                  共 {filteredResults.length} 条
                </span>
                <div className="flex items-center space-x-2">
                  <button 
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-2 py-1 text-xs border border-gray-200 dark:border-slate-700 rounded bg-white dark:bg-slate-800 disabled:opacity-50 text-gray-700 dark:text-slate-300"
                  >
                    上一页
                  </button>
                  <span className="text-xs text-gray-600 dark:text-slate-300">
                    {currentPage} / {totalPages}
                  </span>
                  <button 
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-2 py-1 text-xs border border-gray-200 dark:border-slate-700 rounded bg-white dark:bg-slate-800 disabled:opacity-50 text-gray-700 dark:text-slate-300"
                  >
                    下一页
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Detail Pane */}
        {selectedResult && (
          <div className="flex-1 flex flex-col bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
            <div className="p-6 border-b border-gray-200 dark:border-slate-700 shrink-0 flex justify-between items-start">
              <div>
                <div className="flex items-center space-x-3 mb-3">
                  {getEngineIcon(selectedResult.platform) ? (
                    <div className="flex items-center space-x-1.5 px-3 py-1 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                      <img src={getEngineIcon(selectedResult.platform)} alt={selectedResult.platform} className="w-4 h-4 object-contain" />
                      <span className="text-xs font-medium text-blue-800 dark:text-blue-400">{selectedResult.platform}</span>
                    </div>
                  ) : (
                    <span className="px-3 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 rounded-full text-xs font-medium">
                      {selectedResult.platform}
                    </span>
                  )}
                  {getSentimentBadge(selectedResult.sentiment, selectedResult.mentioned)}
                </div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{selectedResult.question}</h2>
                <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-slate-400">
                  <span>抓取时间: {selectedResult.time}</span>
                  <span>模拟人设: {selectedResult.persona}</span>
                  <span>侧重维度: {selectedResult.theme}</span>
                </div>
              </div>
              <button 
                onClick={() => setSelectedResult(null)}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-slate-300"
              >
                <ArrowLeft size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-8">
              {/* AI Answer */}
              <section>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                  <MessageSquare size={16} className="mr-2 text-blue-500" />
                  引擎原始回答
                </h3>
                <div className="bg-gray-50 dark:bg-slate-900/50 rounded-xl p-5 border border-gray-100 dark:border-slate-700">
                  <div className="prose prose-sm dark:prose-invert max-w-none text-gray-700 dark:text-slate-300 leading-relaxed [&_li_p]:my-0">
                    <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                      {(() => {
                        let cleanAns = selectedResult.answer || '';
                        cleanAns = cleanAns.replace(/\>\s*.*商品卡片.*(?:(?:\r?\n)\>.*)*/g, '');
                        cleanAns = cleanAns.replace(/```json[\s\S]*?```/g, '');
                        cleanAns = cleanAns.replace(/\[\s*\{[\s\S]*\}\s*\]\s*$/, '');
                        cleanAns = cleanAns.replace(/::dqa:(quote|image|link|list|knowledge):[\s\S]*?::/g, '');
                        cleanAns = cleanAns.replace(/::dqa:underline:(.*?)::/g, (match, p1) => {
                            try {
                                const obj = JSON.parse(p1);
                                return obj.text ? obj.text : p1;
                            } catch(e) { return match; }
                        });
                        cleanAns = cleanAns.replace(/(\d+\.[^\n]+)\n+(1\.\s)/g, '$1\n\n---\n\n$2');
                        cleanAns = cleanAns.replace(/\n{3,}/g, '\n\n');
                        return cleanAns.trim();
                      })()}
                    </ReactMarkdown>

                            {/* Product Cards UI */}
                            {selectedResult.productCards && (typeof selectedResult.productCards === 'string' ? JSON.parse(selectedResult.productCards).length > 0 : selectedResult.productCards.length > 0) && (
                                <div className="mt-4 space-y-3">
                                    <div className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 font-bold mb-2">
                                        <ShoppingBag className="w-4 h-4" />
                                        <span>引擎在此回答中动态插入了以下商品导购卡片：</span>
                                    </div>
                                    <div className="flex flex-wrap gap-4">
                                        {(() => {
                                            let parsedCards = selectedResult.productCards;
                                            if (typeof parsedCards === 'string') {
                                                try { parsedCards = JSON.parse(parsedCards); } catch(e) {}
                                            }
                                            return Array.isArray(parsedCards) ? parsedCards.map((card, idx) => (
                                                <div key={idx} className="flex bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-sm hover:shadow-md transition-shadow p-2 w-[280px] gap-3 relative group">
                                                    <div className="w-16 h-16 flex-shrink-0 rounded-md overflow-hidden bg-white dark:bg-slate-800 flex items-center justify-center border border-gray-100 dark:border-slate-600">
                                                        {card.imageUrl ? <img src={card.imageUrl} alt={card.productName} className="w-full h-full object-contain object-center group-hover:scale-105 transition-transform cursor-pointer" onClick={() => setZoomedImage(card.imageUrl)} /> : <div className="w-full h-full flex items-center justify-center text-xs text-gray-400">无图</div>}
                                                    </div>
                                                    <div className="flex flex-col justify-between overflow-hidden">
        <div>
            <div className="text-sm font-bold text-gray-800 dark:text-slate-200 truncate" title={card.productName}>{card.productName}</div>
            <div className="text-[10px] text-gray-500 dark:text-slate-400 line-clamp-2 leading-tight mt-0.5" title={card.details}>{card.details}</div>
        </div>
        <div className="mt-1 flex items-center justify-between">
            <span className="inline-block bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 text-[9px] px-1.5 py-0.5 rounded font-medium truncate max-w-[120px]">{card.merchant}</span>
            
            {/* Crop Button */}
            {selectedResult && selectedResult.sources && selectedResult.sources.length > 0 && (selectedResult.engineId === 'qwmb' || selectedResult.engineId === 'tyqw' || (selectedResult.engineName && selectedResult.engineName.toLowerCase().includes('qianwen'))) && (
              <button
                onClick={(e) => {
                    e.stopPropagation();
                    const srcObj = selectedResult.sources.find(s => s.url || s.link || s.URL || s.Url) || selectedResult.sources[0]; setCropTarget({ recordId: selectedResult.id, cardIndex: idx, sourceUrl: srcObj.url || srcObj.link || srcObj.URL || srcObj.Url });
                }}
                className="ml-2 bg-blue-50 hover:bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:hover:bg-blue-800/50 dark:text-blue-400 p-1 rounded transition-colors"
                title="手工校准截图"
              >
                <Crop className="w-3 h-3" />
              </button>
            )}
        </div>
    </div>
                                                </div>
                                            )) : null;
                                        })()}
                                    </div>
                                </div>
                            )}

                            {/* 小红书 AIO 特色 UI：品牌萃取卡片 */}
                            {selectedResult.xhsBrandDetails && selectedResult.xhsBrandDetails.length > 0 && (
                              <div className="mt-4 mb-4">
                                <div className="flex items-center gap-1.5 text-xs text-[#ff2442] font-bold mb-3">
                                  <svg className="w-4 h-4" viewBox="0 0 1024 1024" fill="currentColor">
                                    <path d="M512 512m-512 0a512 512 0 1 0 1024 0 512 512 0 1 0-1024 0Z" fill="#ff2442"/>
                                    <path d="M512 841.142857C329.142857 841.142857 182.857143 694.857143 182.857143 512S329.142857 182.857143 512 182.857143s329.142857 146.285714 329.142857 329.142857-146.285714 329.142857-329.142857 329.142857z" fill="#ff2442"/>
                                    <path d="M725.333333 460.8v102.4H306.041143v-102.4h419.29219z" fill="#ffffff"/>
                                    <path d="M563.2 292.571429v438.857142h-102.4V292.571429h102.4z" fill="#ffffff"/>
                                  </svg>
                                  <span>小红书 AI 专属总结 (AIO)</span>
                                  {selectedResult.xhsBrandDetails[0]?.answerType && (
                                      <div className="ml-2 text-[10px] bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 px-1.5 py-0.5 rounded-sm border border-blue-100 dark:border-blue-800/50 inline-block">
                                        {selectedResult.xhsBrandDetails[0].answerType}
                                      </div>
                                  )}
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  {selectedResult.xhsBrandDetails.filter(b => b.brandName || b.mentionContent).map((bDetail, idx) => (
                                    <div key={idx} className="bg-red-50/40 dark:bg-[#ff2442]/10 border border-red-100 dark:border-red-900/30 rounded-xl p-3 relative overflow-hidden">
                                      <div className="absolute top-0 right-0 bg-[#ff2442] text-white text-[10px] font-black px-2 py-0.5 rounded-bl-lg">
                                        TOP {bDetail.brandRank || '-'}
                                      </div>
                                      <div className="flex items-center gap-2 mb-2">
                                        <div className="font-bold text-slate-800 dark:text-slate-200 text-sm">
                                          {bDetail.brandName || '未命名品牌'}
                                        </div>
                                        {bDetail.recommendRatio && (
                                          <div className="text-[10px] bg-red-100 dark:bg-red-900/50 text-[#ff2442] dark:text-red-300 px-1.5 py-0.5 rounded-sm">
                                            推荐度 {bDetail.recommendRatio}%
                                          </div>
                                        )}
                                      </div>
                                      {bDetail.mentionContent && (
                                        <div className="text-[11px] text-slate-600 dark:text-slate-400 mt-2 p-2 bg-white/60 dark:bg-slate-900/40 rounded border border-red-50 dark:border-red-900/20">
                                          {bDetail.mentionContent}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                  </div>
                </div>
              </section>

              {/* Data Extraction */}
              <section>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                  <Filter size={16} className="mr-2 text-purple-500" />
                  信息抽取分析
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-4">
                    <span className="text-xs text-gray-500 dark:text-slate-400 block mb-1">提及品牌</span>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {selectedResult.brands.length > 0 ? selectedResult.brands.map((b, i) => (
                        <span key={i} className="px-2 py-1 bg-gray-100 dark:bg-slate-700 rounded text-xs text-gray-700 dark:text-slate-300">
                          {b}
                        </span>
                      )) : <span className="text-sm text-gray-500">无明显提及</span>}
                    </div>
                  </div>
                  <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-4">
                    <span className="text-xs text-gray-500 dark:text-slate-400 block mb-1">情感倾向</span>
                    <div className="mt-2 font-medium text-sm text-gray-900 dark:text-white">
                      {selectedResult.sentiment === 'positive' ? '正向 (推荐)' : selectedResult.sentiment === 'negative' ? '负向 (贬低)' : selectedResult.sentiment === 'controversial' ? '争议 (多面)' : '中立 (客观)'}
                    </div>
                  </div>
                </div>
              </section>

              {/* Citations/References */}
              {selectedResult.xhsRawNotes && selectedResult.xhsRawNotes.length > 0 && (
                  <section className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                      <ExternalLink size={16} className="mr-2 text-[#ff2442]" />
                      AIO 引用溯源 (小红书笔记)
                    </h3>
                    <div className="columns-1 sm:columns-2 gap-4 space-y-4">
                      {selectedResult.xhsRawNotes.map((note, idx) => (
                        <div key={idx} className="break-inside-avoid bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow group cursor-pointer relative" onClick={() => window.open(note.noteUrl || '#', '_blank')}>
                          <div className="absolute top-2 right-2 flex flex-col gap-1 z-10 pointer-events-none opacity-90">
                            <div className="bg-black/50 backdrop-blur text-white text-[9px] px-1.5 py-0.5 rounded-md flex items-center gap-1">
                              <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path></svg>
                              {note.likeCount || 0}
                            </div>
                          </div>
                          <div className="p-3">
                            <div className="text-xs font-bold text-slate-800 dark:text-slate-200 line-clamp-2 mb-1.5 leading-snug group-hover:text-[#ff2442] transition-colors">
                              {note.title || '无标题笔记'}
                            </div>
                            <div className="text-[10px] text-slate-500 dark:text-slate-400 line-clamp-3 mb-2">
                              {note.content}
                            </div>
                            <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100 dark:border-slate-700/50">
                              <div className="flex items-center gap-1.5">
                                <div className="w-4 h-4 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center overflow-hidden">
                                  <svg className="w-2.5 h-2.5 text-slate-400" fill="currentColor" viewBox="0 0 24 24"><path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                                </div>
                                <span className="text-[10px] text-slate-500 dark:text-slate-400 truncate max-w-[80px]">{note.nickname || '小红书用户'}</span>
                              </div>
                              {note.timeText && <span className="text-[9px] text-slate-400">{note.timeText}</span>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
              )}
              
              {selectedResult.sources && selectedResult.sources.length > 0 && (
                <section>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                    <ExternalLink size={16} className="mr-2 text-green-500" />
                    {(selectedResult.engineId === 'xhs' || selectedResult.engineId === '小红书' || selectedResult.engineId === '小红书AI' || selectedResult.engineId === '小红书ai') ? '小红书自然排名笔记 (Organic Results)' : '引用的信源'}
                  </h3>
                  <div className="space-y-3">
                    {selectedResult.sources
                        .filter(src => src.name !== '小红书AI回答' && src.title !== '小红书AI回答' && src.url !== '小红书AI回答')
                        .map((source, i) => {
                            const displayName = source.title || source.name || '未知来源';
                            const sourceUrl = source.url || source.link;
                            return sourceUrl && sourceUrl !== 'brand_detail' ? (
                              <a href={sourceUrl} target="_blank" rel="noreferrer" key={i} className="flex items-center justify-between p-3 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg hover:border-blue-300 transition-colors cursor-pointer group">
                                <div className="flex flex-col">
                                  <span className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-blue-500 transition-colors line-clamp-2" title={displayName}>
                                    {displayName}
                                  </span>
                                  <span className="text-xs text-gray-500 dark:text-slate-400 mt-1">来源: {source.name || source.mediaName || '未知'}</span>
                                </div>
                                <ChevronRight size={16} className="text-gray-400 group-hover:text-blue-500" />
                              </a>
                            ) : (
                                <div key={i} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg">
                                  <div className="flex flex-col">
                                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300 line-clamp-2" title={displayName}>
                                      {displayName}
                                    </span>
                                    <span className="text-xs text-gray-500 dark:text-slate-400 mt-1">来源: {source.name || source.mediaName || '未知'}</span>
                                  </div>
                                </div>
                            );
                    })}
                  </div>
                </section>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Zoomed Image Modal */}
      
            {cropTarget && (
                <ImageCropperModal
                    imageUrl={cropTarget.sourceUrl}
                    onClose={() => setCropTarget(null)}
                    onCropComplete={handleCropComplete}
                />
            )}
  
            {zoomedImage && (
        <div 
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={() => setZoomedImage(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh] w-full h-full flex items-center justify-center">
            <button 
              className="absolute top-4 right-4 text-white hover:text-gray-300 z-50 p-2"
              onClick={(e) => { e.stopPropagation(); setZoomedImage(null); }}
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
            </button>
            <img 
              src={zoomedImage} 
              alt="Zoomed Product" 
              className="max-w-full max-h-[90vh] object-contain rounded shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

    </div>
  );
}
